import { create } from "zustand";
import {
  signIn,
  signOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  type SignInOutput,
} from "aws-amplify/auth";

/**
 * Sanitize Cognito/Amplify error messages to prevent leaking sensitive data.
 * The SDK can include challengeResponse values (which may be passwords) in error messages.
 */
function sanitizeAuthError(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  const msg = error.message;
  if (msg.toLowerCase().includes("challengeresponse")) {
    return fallback;
  }
  return msg;
}

// Error thrown when user is not in the admins Cognito group
export class NotAdminError extends Error {
  constructor() {
    super("Access denied. You must be a system administrator to access this application.");
    this.name = "NotAdminError";
  }
}

// Helper to check if user is in "admins" Cognito group
function isInAdminsGroup(idToken: { payload: Record<string, unknown> }): boolean {
  const groups = idToken.payload["cognito:groups"];
  if (Array.isArray(groups)) {
    return groups.includes("admins");
  }
  return false;
}

interface User {
  id: string;
  email: string;
  name?: string;
  isAdmin: boolean;
}

type AuthStep = "idle" | "totp" | "new_password" | "totp_setup" | "forgot_password" | "reset_password";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  authStep: AuthStep;
  pendingEmail: string | null;

  totpSetupSecret: string | null;
  resetSuccess: boolean;

  login: (email: string, password: string) => Promise<SignInOutput>;
  confirmTotp: (code: string) => Promise<void>;
  confirmNewPassword: (newPassword: string) => Promise<void>;
  setupTotp: (code: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  resetAuthStep: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      authStep: "idle" as AuthStep,
      pendingEmail: null,
      totpSetupSecret: null,
      resetSuccess: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          let signInResult;
          try {
            signInResult = await signIn({ username: email, password });
          } catch (err) {
            // If already signed in, sign out first and retry
            if (err instanceof Error && err.message.includes("already a signed in user")) {
              await signOut();
              signInResult = await signIn({ username: email, password });
            } else {
              throw err;
            }
          }
          // Check if sign-in is complete
          if (signInResult.isSignedIn) {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken;

            // Check if user is in the "admins" Cognito group
            if (!idToken || !isInAdminsGroup(idToken)) {
              await signOut();
              throw new NotAdminError();
            }

            const currentUser = await getCurrentUser();
            set({
              user: {
                id: currentUser.userId,
                email: currentUser.signInDetails?.loginId || email,
                isAdmin: true,
              },
              token: idToken.toString(),
              isAuthenticated: true,
              isLoading: false,
              authStep: "idle",
            });
          } else {
            // Sign-in not complete (e.g., MFA required, new password required, MFA setup)
            const nextStep = signInResult.nextStep?.signInStep;
            if (nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
              set({ isLoading: false, authStep: "totp", pendingEmail: email });
            } else if (nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
              set({ isLoading: false, authStep: "new_password", pendingEmail: email });
            } else if (nextStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
              const secret = signInResult.nextStep?.totpSetupDetails?.sharedSecret || null;
              set({ isLoading: false, authStep: "totp_setup", totpSetupSecret: secret, pendingEmail: email });
            } else if (nextStep === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION") {
              // Auto-select TOTP when Cognito asks which MFA method to set up
              const mfaResult = await confirmSignIn({ challengeResponse: "TOTP" });
              if (mfaResult.nextStep?.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
                const secret = mfaResult.nextStep?.totpSetupDetails?.sharedSecret || null;
                set({ isLoading: false, authStep: "totp_setup", totpSetupSecret: secret, pendingEmail: email });
              } else {
                set({ isLoading: false });
              }
            } else {
              set({ isLoading: false });
            }
          }

          return signInResult;
        } catch (error) {
          set({
            error: sanitizeAuthError(error, "Login failed"),
            isLoading: false,
          });
          throw error;
        }
      },

      confirmTotp: async (code: string) => {
        set({ isLoading: true, error: null });
        try {
          const result = await confirmSignIn({ challengeResponse: code });
          if (result.isSignedIn) {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken;

            // Check if user is in the "admins" Cognito group
            if (!idToken || !isInAdminsGroup(idToken)) {
              await signOut();
              throw new NotAdminError();
            }

            const currentUser = await getCurrentUser();
            set({
              user: {
                id: currentUser.userId,
                email: currentUser.signInDetails?.loginId || "",
                isAdmin: true,
              },
              token: idToken.toString(),
              isAuthenticated: true,
              isLoading: false,
              authStep: "idle",
              pendingEmail: null,
            });
          } else {
            set({ isLoading: false });
          }
        } catch (error) {
          set({
            error: sanitizeAuthError(error, "Invalid code"),
            isLoading: false,
          });
          throw error;
        }
      },

      confirmNewPassword: async (newPassword: string) => {
        set({ isLoading: true, error: null });
        try {
          const result = await confirmSignIn({ challengeResponse: newPassword });
          if (result.isSignedIn) {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken;

            if (!idToken || !isInAdminsGroup(idToken)) {
              await signOut();
              throw new NotAdminError();
            }

            const currentUser = await getCurrentUser();
            set({
              user: {
                id: currentUser.userId,
                email: currentUser.signInDetails?.loginId || "",
                isAdmin: true,
              },
              token: idToken.toString(),
              isAuthenticated: true,
              isLoading: false,
              authStep: "idle",
              pendingEmail: null,
            });
          } else {
            // Handle next challenge after password change (e.g., MFA setup)
            const nextStep = result.nextStep?.signInStep;
            if (nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
              set({ isLoading: false, authStep: "totp" });
            } else if (nextStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
              const secret = result.nextStep?.totpSetupDetails?.sharedSecret || null;
              set({ isLoading: false, authStep: "totp_setup", totpSetupSecret: secret });
            } else if (nextStep === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION") {
              const mfaResult = await confirmSignIn({ challengeResponse: "TOTP" });
              if (mfaResult.nextStep?.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
                const secret = mfaResult.nextStep?.totpSetupDetails?.sharedSecret || null;
                set({ isLoading: false, authStep: "totp_setup", totpSetupSecret: secret });
              } else {
                set({ isLoading: false });
              }
            } else {
              set({ isLoading: false });
            }
          }
        } catch (error) {
          set({
            error: sanitizeAuthError(error, "Failed to set password"),
            isLoading: false,
          });
          throw error;
        }
      },

      setupTotp: async (code: string) => {
        set({ isLoading: true, error: null });
        try {
          const result = await confirmSignIn({ challengeResponse: code });
          if (result.isSignedIn) {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken;

            if (!idToken || !isInAdminsGroup(idToken)) {
              await signOut();
              throw new NotAdminError();
            }

            const currentUser = await getCurrentUser();
            set({
              user: {
                id: currentUser.userId,
                email: currentUser.signInDetails?.loginId || "",
                isAdmin: true,
              },
              token: idToken.toString(),
              isAuthenticated: true,
              isLoading: false,
              authStep: "idle",
              pendingEmail: null,
              totpSetupSecret: null,
            });
          } else {
            set({ isLoading: false });
          }
        } catch (error) {
          set({
            error: sanitizeAuthError(error, "Invalid verification code"),
            isLoading: false,
          });
          throw error;
        }
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          await resetPassword({ username: email });
          set({
            isLoading: false,
            authStep: "reset_password",
            pendingEmail: email,
          });
        } catch (error) {
          set({
            error: sanitizeAuthError(error, "Failed to send reset code"),
            isLoading: false,
          });
          throw error;
        }
      },

      confirmPasswordReset: async (code: string, newPassword: string) => {
        set({ isLoading: true, error: null });
        const email = useAuthStore.getState().pendingEmail;
        if (!email) {
          set({ error: "No email address found. Please start over.", isLoading: false });
          return;
        }
        try {
          await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword,
          });
          set({
            isLoading: false,
            authStep: "idle",
            pendingEmail: null,
            resetSuccess: true,
          });
        } catch (error) {
          set({
            error: sanitizeAuthError(error, "Failed to reset password"),
            isLoading: false,
          });
          throw error;
        }
      },

      resetAuthStep: () => {
        // Clean up any partial Cognito sign-in session to avoid stale state
        signOut().catch(() => {});
        set({ authStep: "idle", pendingEmail: null, error: null, totpSetupSecret: null, resetSuccess: false });
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await signOut();
        } finally {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            authStep: "idle",
            pendingEmail: null,
          });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });

        try {
          // Try to get the session from Amplify's storage
          const session = await fetchAuthSession({ forceRefresh: false });

          // Check if we have valid tokens
          const idToken = session.tokens?.idToken;
          if (!idToken) {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }

          // Check if user is in the "admins" Cognito group
          if (!isInAdminsGroup(idToken)) {
            await signOut();
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }

          const currentUser = await getCurrentUser();

          set({
            user: {
              id: currentUser.userId,
              email: currentUser.signInDetails?.loginId || "",
              isAdmin: true,
            },
            token: idToken.toString(),
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error("checkAuth error:", error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      clearError: () => set({ error: null }),
}));
